import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import dayjs from "dayjs";

export default function Account() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [donations, setDonations] = useState([]);
  const [apps, setApps] = useState({ retreats: [], volunteers: [] });

  useEffect(() => {
    if (loading) return;
    if (!user || user.provider !== "google") { nav("/", { replace: true }); return; }
    api.get("/me/donations").then(r => setDonations(r.data)).catch(() => {});
    api.get("/me/applications").then(r => setApps(r.data)).catch(() => {});
  }, [user, loading, nav]);

  if (loading || !user) return <div className="min-h-[50vh] flex items-center justify-center text-ink-soft">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 space-y-10" data-testid="account-page">
      <div className="flex items-center gap-6">
        {user.picture && <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border border-line" />}
        <div>
          <div className="overline">My Account</div>
          <h1 className="font-serif text-4xl mt-1">{user.name}</h1>
          <p className="text-ink-soft text-sm">{user.email}</p>
        </div>
        <button onClick={async () => { await logout(); nav("/"); }} data-testid="account-signout" className="ml-auto pill pill-outline text-sm">Sign out</button>
      </div>

      <section className="bg-surface border border-line rounded-2xl p-8">
        <div className="overline mb-4">Donation History</div>
        {donations.length ? (
          <div className="divide-y divide-line">
            {donations.map(d => (
              <div key={d.session_id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-ink">${d.amount} — {(d.fund || "general").replace("_"," ")}</div>
                  <div className="text-xs text-ink-soft">{dayjs(d.created_at).format("MMM D, YYYY")} · {d.monthly ? "monthly" : "one-time"}</div>
                </div>
                <div className={`text-xs uppercase tracking-widest ${d.payment_status === "paid" ? "text-forest" : "text-ink-soft"}`}>{d.payment_status}</div>
              </div>
            ))}
          </div>
        ) : <div className="text-ink-soft text-sm">No donations yet.</div>}
      </section>

      <section className="bg-surface border border-line rounded-2xl p-8">
        <div className="overline mb-4">Retreat Applications</div>
        {apps.retreats.length ? apps.retreats.map(r => (
          <div key={r.id} className="py-3 border-t border-line first:border-t-0 flex items-center justify-between">
            <div>
              <div className="font-medium">{r.retreat_title || "Retreat"}</div>
              <div className="text-xs text-ink-soft">{dayjs(r.created_at).format("MMM D, YYYY")}</div>
            </div>
            <div className="text-xs uppercase tracking-widest text-terracotta">{r.status}</div>
          </div>
        )) : <div className="text-ink-soft text-sm">No retreat applications yet.</div>}
      </section>

      <section className="bg-surface border border-line rounded-2xl p-8">
        <div className="overline mb-4">Volunteer Applications</div>
        {apps.volunteers.length ? apps.volunteers.map(v => (
          <div key={v.id} className="py-3 border-t border-line first:border-t-0 flex items-center justify-between">
            <div>
              <div className="font-medium">{v.country || "Volunteer"} · {v.availability || ""}</div>
              <div className="text-xs text-ink-soft">{dayjs(v.created_at).format("MMM D, YYYY")}</div>
            </div>
            <div className="text-xs uppercase tracking-widest text-terracotta">{v.status}</div>
          </div>
        )) : <div className="text-ink-soft text-sm">No volunteer applications yet.</div>}
      </section>
    </div>
  );
}
